import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, statusLabel } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

const TABS = [
  { id: "evenements", label: "Événements" },
  { id: "reservations", label: "Réservations" },
  { id: "devis", label: "Devis" },
  { id: "demandes", label: "Demandes" },
  { id: "paiements", label: "Paiements" },
  { id: "messages", label: "Messages" },
  { id: "rdv", label: "Rendez-vous" },
  { id: "documents", label: "Documents" },
  { id: "historique", label: "Chronologie" },
  { id: "notes", label: "Notes internes" },
];

const QUOTE_LABELS = { brouillon: "Brouillon", envoye: "En attente", accepte: "Répondu", refuse: "Annulée", expire: "Passée" };
const APPT_LABELS = { demande: "Nouvelle demande", planifie: "En attente", confirme: "Confirmée", annule: "Annulée", effectue: "Passée" };

export default function ClientDetailPage() {
  const { customerId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("evenements");
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/customers/${customerId}`).then((r) => setData(r.data)).catch(() => setData(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [customerId]);

  const addNote = async () => {
    try { await api.post(`/admin/customers/${customerId}/notes`, { note }); setNote(""); toast.success("Note interne ajoutée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (data === null) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="client-file-loading">Chargement…</div>;
  if (data === false) return <EmptyState title="Client introuvable" />;
  const c = data.customer;

  return (
    <div data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-sm font-semibold text-deepgreen hover:text-sage" data-testid="client-file-back">
        <ArrowLeft className="h-4 w-4" /> Clients
      </Link>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sage-light font-heading text-lg font-semibold text-deepgreen">
          {c.first_name?.[0]}{c.last_name?.[0]}
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-deepgreen" data-testid="client-file-name">{c.first_name} {c.last_name}</h1>
          <p className="text-sm text-muted-foreground">{c.email}{c.phone ? ` — ${c.phone}` : ""}</p>
        </div>
      </div>

      <div className="mt-6 flex gap-1 overflow-x-auto border-b border-border" data-testid="client-file-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`client-tab-${t.id}`}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "border-deepgreen text-deepgreen" : "border-transparent text-muted-foreground hover:text-deepgreen"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "evenements" && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="client-events">
            {data.events.length === 0 && <EmptyState title="Aucun événement" />}
            {data.events.map((e) => (
              <div key={e.id} className="card-ae p-5" data-testid={`client-event-${e.id}`}>
                <p className="font-heading text-lg text-deepgreen">{e.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{e.date_start}{e.date_end ? ` → ${e.date_end}` : ""} — {e.venue_address || "Lieu à préciser"}</p>
                <div className="mt-2 flex gap-2">
                  <span className="rounded-full bg-sage-light px-3 py-1 text-xs font-semibold text-deepgreen">Location</span>
                  {e.has_coordination && <span className="rounded-full bg-gold-light px-3 py-1 text-xs font-semibold text-deepgreen">Coordination</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === "reservations" && (
          <div className="space-y-3" data-testid="client-reservations">
            {data.reservations.length === 0 && <EmptyState title="Aucune réservation" />}
            {data.reservations.map((r) => (
              <Link key={r.id} to={`/admin/reservations/${r.id}`} className="card-ae flex flex-wrap items-center justify-between gap-3 p-4 transition-colors hover:bg-cream/60" data-testid={`client-reservation-${r.ref}`}>
                <div><p className="font-semibold text-deepgreen">{r.ref}</p><p className="text-xs text-muted-foreground">{r.event?.start_date} → {r.event?.end_date}</p></div>
                <p className="text-sm font-semibold">{(r.totals?.total_ttc ?? 0).toFixed(2)} € TTC</p>
                <StatusBadge status={statusLabel(r.status)} />
              </Link>
            ))}
          </div>
        )}
        {tab === "devis" && (
          <div className="space-y-3" data-testid="client-quotes">
            {data.quotes.length === 0 && <EmptyState title="Aucun devis" />}
            {data.quotes.map((q) => (
              <div key={q.id} className="card-ae flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`client-quote-${q.ref}`}>
                <div><p className="font-semibold text-deepgreen">{q.ref} <span className="text-xs text-muted-foreground">v{q.version}</span></p></div>
                <p className="text-sm font-semibold">{(q.totals?.total_ttc ?? 0).toFixed(2)} €</p>
                <StatusBadge status={QUOTE_LABELS[q.status] || q.status} />
              </div>
            ))}
          </div>
        )}
        {tab === "messages" && (
          <div className="space-y-3" data-testid="client-threads">
            {data.threads.length === 0 && <EmptyState title="Aucune conversation" />}
            {data.threads.map((t) => (
              <Link key={t.id} to="/admin/messages" className="card-ae flex items-center justify-between gap-3 p-4 transition-colors hover:bg-cream/60" data-testid={`client-thread-${t.id}`}>
                <div><p className="font-semibold text-deepgreen">{t.subject}</p><p className="text-xs text-muted-foreground">Dernier message : {(t.last_message_at || "").slice(0, 16).replace("T", " ")}</p></div>
                {t.unread_admin > 0 && <span className="rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold text-deepgreen">{t.unread_admin} non lu(s)</span>}
              </Link>
            ))}
          </div>
        )}
        {tab === "rdv" && (
          <div className="space-y-3" data-testid="client-appointments">
            {data.appointments.length === 0 && <EmptyState title="Aucun rendez-vous" />}
            {data.appointments.map((a) => (
              <div key={a.id} className="card-ae flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`client-appointment-${a.id}`}>
                <div><p className="font-semibold text-deepgreen">{a.type}</p><p className="text-xs text-muted-foreground">{a.date} — {a.slot}</p></div>
                <StatusBadge status={APPT_LABELS[a.status] || a.status} />
              </div>
            ))}
          </div>
        )}
        {tab === "documents" && (
          <div className="space-y-3" data-testid="client-documents">
            {data.documents.length === 0 && <EmptyState title="Aucun document" />}
            {data.documents.map((d) => (
              <div key={d.id} className="card-ae flex items-center justify-between gap-3 p-4" data-testid={`client-document-${d.id}`}>
                <p className="text-sm font-semibold text-deepgreen">{d.label}</p>
                <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">{d.kind}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "demandes" && (
          <div className="space-y-3" data-testid="client-leads">
            {(data.demandes || []).length === 0 && <EmptyState title="Aucune demande" />}
            {(data.demandes || []).map((d) => (
              <Link key={d.id} to="/admin/messages" className="card-ae flex flex-wrap items-center justify-between gap-3 p-4 transition-colors hover:bg-cream/60" data-testid={`client-lead-${d.id}`}>
                <div>
                  <p className="font-semibold text-deepgreen">{d.subject || "Demande de contact"}</p>
                  <p className="text-xs text-muted-foreground">{(d.created_at || "").slice(0, 16).replace("T", " ")} — via {d.source === "accueil" ? "accueil" : "page Contact"}{d.event_date ? ` — événement le ${d.event_date}` : d.event_date_tbd ? " — date à définir" : ""}</p>
                </div>
                <StatusBadge status={d.status} />
              </Link>
            ))}
          </div>
        )}
        {tab === "paiements" && (
          <div className="space-y-3" data-testid="client-payments">
            {(data.payments || []).length === 0 && <EmptyState title="Aucun paiement" />}
            {(data.payments || []).map((p) => (
              <div key={p.id} className="card-ae flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`client-payment-${p.id}`}>
                <div><p className="font-semibold text-deepgreen">{p.type} — {(p.amount ?? 0).toFixed(2)} €</p><p className="text-xs text-muted-foreground">{(p.created_at || "").slice(0, 16).replace("T", " ")}</p></div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        )}
        {tab === "historique" && (
          <div className="card-ae p-5" data-testid="client-history">
            <h3 className="font-heading text-lg text-deepgreen">Chronologie des interactions</h3>
            {(data.timeline || []).length === 0 && <EmptyState title="Aucune activité" />}
            <ul className="mt-4 space-y-0">
              {(data.timeline || []).map((t, i) => (
                <li key={i} className="relative border-l-2 border-sage pb-4 pl-5" data-testid={`client-timeline-${i}`}>
                  <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-gold" aria-hidden />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {t.link ? (
                      <Link to={t.link} className="text-sm font-semibold text-deepgreen hover:text-sage">{t.label}</Link>
                    ) : (
                      <span className="text-sm font-semibold text-deepgreen">{t.label}</span>
                    )}
                    <span className="text-xs text-muted-foreground">{(t.at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.kind}</p>
                </li>
              ))}
            </ul>
            {(data.history || []).length > 0 && (
              <>
                <h4 className="mt-6 font-heading text-base text-deepgreen">Actions du client (audit)</h4>
                <ul className="mt-2 space-y-2 text-sm">
                  {data.history.map((h) => (
                    <li key={h.id} className="flex justify-between gap-3 border-b border-border pb-2">
                      <span className="text-foreground/80">{h.action} — {h.entity}</span>
                      <span className="text-xs text-muted-foreground">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
        {tab === "notes" && (
          <div className="card-ae p-5" data-testid="client-notes">
            <h3 className="font-heading text-lg text-deepgreen">Notes internes — jamais visibles par le client</h3>
            <div className="mt-4 flex gap-2">
              <input className="input-ae" placeholder="Nouvelle note interne…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="client-note-input" />
              <button type="button" onClick={addNote} disabled={note.length < 2} className="btn-primary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="client-note-add">
                <Plus className="h-4 w-4" /> Ajouter
              </button>
            </div>
            <ul className="mt-4 space-y-2">
              {data.notes.map((n) => (
                <li key={n.id} className="rounded-lg bg-cream p-3 text-sm" data-testid={`client-note-${n.id}`}>
                  {n.note}
                  <span className="ml-2 text-xs text-muted-foreground">{n.author_email} — {(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
